// StudyDost — Parent Portal Pages
// frontend/src/pages/ParentDashboard.js
// Route: /parent/dashboard (after login)

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Flame, Star, Clock, TrendingUp,
  ChevronRight, Link, LogOut, BarChart2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const SUBJECT_CONFIG = {
  MATH:     { emoji: '🧮', color: '#4F46E5', label: 'Math' },
  PHYSICS:  { emoji: '⚡', color: '#0891B2', label: 'Physics' },
  CHEMISTRY:{ emoji: '🧪', color: '#D97706', label: 'Chemistry' },
  BIOLOGY:  { emoji: '🌿', color: '#059669', label: 'Biology' },
  SCIENCE:  { emoji: '🔬', color: '#7C3AED', label: 'Science' },
  ENGLISH:  { emoji: '📖', color: '#BE185D', label: 'English' },
  HINDI:    { emoji: '🔤', color: '#C2410C', label: 'Hindi' },
  HISTORY:  { emoji: '📜', color: '#92400E', label: 'History' },
  GEOGRAPHY:{ emoji: '🗺️', color: '#065F46', label: 'Geography' },
  OTHER:    { emoji: '📝', color: '#475569', label: 'General' },
};

const PLAN_LABELS = {
  FREE: { label: 'Free Plan', color: '#64748B' },
  MONTHLY: { label: 'Monthly Plan ✨', color: '#4F46E5' },
  QUARTERLY: { label: 'Quarterly Plan', color: '#7C3AED' },
  ANNUAL: { label: 'Annual Plan ⭐', color: '#D97706' },
};

export default function ParentDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedChild, setSelectedChild] = useState(null);
  const [familyCode, setFamilyCode] = useState('');
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [linkSuccess, setLinkSuccess] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['parentDashboard'],
    queryFn: () => api.get('/parent/dashboard').then(r => r.data),
    retry: false,
  });

  const children = data?.children || [];
  const activeChild = selectedChild
    ? children.find(c => c.id === selectedChild)
    : children[0];

  const handleLinkChild = async () => {
    if (!familyCode.trim()) return;
    setLinking(true);
    setLinkError('');
    try {
      const res = await api.post('/parent/link-child', {
        familyCode: familyCode.trim().toUpperCase(),
      });
      if (res.data.success) {
        setLinkSuccess(`✅ ${res.data.child.name || 'Child'} linked successfully!`);
        setFamilyCode('');
        refetch();
      }
    } catch (err) {
      setLinkError(err.response?.data?.error || 'Invalid code. Ask your child for their family code.');
    } finally {
      setLinking(false);
    }
  };

  if (isLoading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner} />
        <p style={{ color: '#6B7280', marginTop: 12 }}>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerBg} />
        <div style={styles.headerContent}>
          <div style={styles.headerTop}>
            <div>
              <h1 style={styles.headerTitle}>👨‍👩‍👦 Parent Dashboard</h1>
              <p style={styles.headerSub}>StudyDost — Aapke bachche ki progress</p>
            </div>
            <button style={styles.logoutBtn} onClick={() => { logout(); navigate('/'); }}>
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>

      <div style={styles.content}>

        {/* No children linked */}
        {children.length === 0 && (
          <div style={styles.emptyCard}>
            <div style={styles.emptyEmoji}>🔗</div>
            <h3 style={styles.emptyTitle}>Bachche ko link karein</h3>
            <p style={styles.emptyDesc}>
              Apne bachche se unka StudyDost family code maangein aur yahan enter karein.
            </p>
            <div style={styles.linkBox}>
              <input
                style={styles.codeInput}
                placeholder="6-digit family code (e.g. ABC123)"
                value={familyCode}
                onChange={e => setFamilyCode(e.target.value.toUpperCase())}
                maxLength={6}
              />
              <button
                style={{ ...styles.linkBtn, opacity: familyCode.length === 6 ? 1 : 0.5 }}
                onClick={handleLinkChild}
                disabled={linking || familyCode.length !== 6}
              >
                {linking ? 'Linking...' : 'Link Karein'}
              </button>
            </div>
            {linkError && <p style={styles.errorText}>{linkError}</p>}
            {linkSuccess && <p style={styles.successText}>{linkSuccess}</p>}
            <p style={styles.hintText}>
              💡 Bachche ko StudyDost pe "FAMILY CODE" type karne ko bolein
            </p>
          </div>
        )}

        {/* Children tabs */}
        {children.length > 1 && (
          <div style={styles.childTabs}>
            {children.map(child => (
              <button
                key={child.id}
                style={{
                  ...styles.childTab,
                  background: activeChild?.id === child.id ? '#4F46E5' : '#F1F5F9',
                  color: activeChild?.id === child.id ? '#FFFFFF' : '#374151',
                }}
                onClick={() => setSelectedChild(child.id)}
              >
                {child.name?.split(' ')[0] || 'Child'}
              </button>
            ))}
          </div>
        )}

        {/* Child dashboard */}
        {activeChild && (
          <>
            {/* Child info card */}
            <div style={styles.childCard}>
              <div style={styles.childAvatar}>
                {(activeChild.name || 'S')[0].toUpperCase()}
              </div>
              <div style={styles.childInfo}>
                <div style={styles.childName}>{activeChild.name || 'Student'}</div>
                <div style={styles.childMeta}>
                  Class {activeChild.studentClass} · {activeChild.board}
                </div>
              </div>
              <div style={{
                ...styles.planTag,
                background: (PLAN_LABELS[activeChild.activePlan]?.color || '#64748B') + '15',
                color: PLAN_LABELS[activeChild.activePlan]?.color || '#64748B',
              }}>
                {PLAN_LABELS[activeChild.activePlan]?.label || 'Free'}
              </div>
            </div>

            {/* Stats row */}
            <div style={styles.statsRow}>
              {[
                { icon: <BookOpen size={18} color="#4F46E5" />, value: activeChild.weeklyQuestionCount, label: 'This Week', bg: '#EEF2FF' },
                { icon: <Flame size={18} color="#EA580C" />, value: `${activeChild.streak} 🔥`, label: 'Streak', bg: '#FFF7ED' },
                { icon: <Star size={18} color="#D97706" />, value: activeChild.totalXP, label: 'XP', bg: '#FFFBEB' },
                { icon: <TrendingUp size={18} color="#059669" />, value: activeChild.totalQuestions, label: 'Total', bg: '#F0FDF4' },
              ].map((stat, i) => (
                <div key={i} style={{ ...styles.statCard, background: stat.bg }}>
                  {stat.icon}
                  <div style={styles.statValue}>{stat.value}</div>
                  <div style={styles.statLabel}>{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Level */}
            <div style={styles.levelCard}>
              <div style={styles.levelLeft}>
                <span style={styles.levelEmoji}>🎓</span>
                <div>
                  <div style={styles.levelTitle}>{activeChild.level}</div>
                  <div style={styles.levelSub}>{activeChild.totalXP} XP earned</div>
                </div>
              </div>
            </div>

            {/* Activity chart */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                <BarChart2 size={16} color="#4F46E5" />
                Daily Activity (Last 7 Days)
              </div>
              <div style={styles.barChart}>
                {activeChild.dailyActivity?.map((day, i) => {
                  const maxCount = Math.max(...(activeChild.dailyActivity?.map(d => d.count) || [1]), 1);
                  const height = Math.max((day.count / maxCount) * 80, 4);
                  return (
                    <div key={i} style={styles.barCol}>
                      <div style={styles.barCount}>{day.count || ''}</div>
                      <div style={{ ...styles.bar, height, background: day.count > 0 ? '#4F46E5' : '#E2E8F0' }} />
                      <div style={styles.barLabel}>{day.label}</div>
                    </div>
                  );
                })}
              </div>
              {activeChild.mostActiveTime && (
                <p style={styles.activeTimeNote}>
                  <Clock size={13} color="#94A3B8" />
                  Most active at {activeChild.mostActiveTime}
                </p>
              )}
            </div>

            {/* Subject breakdown */}
            {activeChild.subjectBreakdown?.length > 0 && (
              <div style={styles.section}>
                <div style={styles.sectionTitle}>
                  <BookOpen size={16} color="#4F46E5" />
                  Subject Activity (This Week)
                </div>
                {activeChild.subjectBreakdown.map((s, i) => {
                  const config = SUBJECT_CONFIG[s.subject] || SUBJECT_CONFIG.OTHER;
                  const maxCount = activeChild.subjectBreakdown[0]?.count || 1;
                  return (
                    <div key={i} style={styles.subjectRow}>
                      <span style={styles.subjectEmoji}>{config.emoji}</span>
                      <span style={styles.subjectName}>{config.label}</span>
                      <div style={styles.subjectBarTrack}>
                        <div style={{
                          ...styles.subjectBarFill,
                          width: `${(s.count / maxCount) * 100}%`,
                          background: config.color,
                        }} />
                      </div>
                      <span style={styles.subjectCount}>{s.count}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add another child */}
            {children.length < 5 && (
              <div style={styles.addChildSection}>
                <div style={styles.addChildTitle}>
                  <Link size={14} color="#4F46E5" />
                  Add Another Child
                </div>
                <div style={styles.linkBox}>
                  <input
                    style={styles.codeInput}
                    placeholder="Family code"
                    value={familyCode}
                    onChange={e => setFamilyCode(e.target.value.toUpperCase())}
                    maxLength={6}
                  />
                  <button
                    style={{ ...styles.linkBtn, opacity: familyCode.length === 6 ? 1 : 0.5 }}
                    onClick={handleLinkChild}
                    disabled={linking || familyCode.length !== 6}
                  >
                    Link
                  </button>
                </div>
                {linkError && <p style={styles.errorText}>{linkError}</p>}
                {linkSuccess && <p style={styles.successText}>{linkSuccess}</p>}
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#F8FAFF', fontFamily: "'DM Sans', sans-serif", paddingBottom: 80 },
  loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' },
  spinner: { width: 36, height: 36, border: '3px solid #E2E8F0', borderTopColor: '#4F46E5', borderRadius: '50%', animation: 'spin 0.7s linear infinite' },

  // Header
  header: { position: 'relative', overflow: 'hidden', padding: '28px 20px 32px' },
  headerBg: { position: 'absolute', inset: 0, background: 'linear-gradient(160deg, #0F172A 0%, #1E3A5F 100%)' },
  headerContent: { position: 'relative', zIndex: 1 },
  headerTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  headerTitle: { fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 800, color: '#FFFFFF', marginBottom: 4 },
  headerSub: { fontSize: 13, color: 'rgba(255,255,255,0.6)' },
  logoutBtn: { background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, padding: 8, cursor: 'pointer', color: '#FFFFFF', display: 'flex' },

  // Content
  content: { padding: '16px' },

  // Empty state
  emptyCard: { background: '#FFFFFF', borderRadius: 20, padding: '32px 20px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' },
  emptyEmoji: { fontSize: 48, marginBottom: 14 },
  emptyTitle: { fontFamily: "'Sora', sans-serif", fontSize: 18, fontWeight: 700, color: '#0F172A', marginBottom: 8 },
  emptyDesc: { fontSize: 14, color: '#64748B', lineHeight: 1.6, marginBottom: 24 },
  linkBox: { display: 'flex', gap: 8, marginBottom: 12 },
  codeInput: { flex: 1, border: '2px solid #E2E8F0', borderRadius: 10, padding: '10px 14px', fontSize: 16, fontWeight: 700, letterSpacing: '0.1em', outline: 'none', textAlign: 'center', fontFamily: 'monospace' },
  linkBtn: { background: '#4F46E5', color: '#FFFFFF', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },
  errorText: { fontSize: 13, color: '#E11D48', marginBottom: 8 },
  successText: { fontSize: 13, color: '#059669', marginBottom: 8 },
  hintText: { fontSize: 12, color: '#94A3B8', lineHeight: 1.5 },

  // Tabs
  childTabs: { display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto' },
  childTab: { flexShrink: 0, border: 'none', borderRadius: 100, padding: '8px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },

  // Child card
  childCard: { background: '#FFFFFF', borderRadius: 16, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
  childAvatar: { width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #4F46E5, #7C3AED)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: '#FFFFFF', flexShrink: 0 },
  childInfo: { flex: 1 },
  childName: { fontSize: 16, fontWeight: 700, color: '#0F172A' },
  childMeta: { fontSize: 12, color: '#94A3B8', marginTop: 2 },
  planTag: { fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 100 },

  // Stats
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 },
  statCard: { borderRadius: 14, padding: '12px 8px', textAlign: 'center' },
  statValue: { fontSize: 18, fontWeight: 800, color: '#0F172A', marginTop: 4, marginBottom: 2 },
  statLabel: { fontSize: 10, color: '#64748B' },

  // Level
  levelCard: { background: 'linear-gradient(135deg, #EEF2FF, #F5F3FF)', borderRadius: 14, padding: '12px 16px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  levelLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  levelEmoji: { fontSize: 28 },
  levelTitle: { fontSize: 15, fontWeight: 700, color: '#3730A3' },
  levelSub: { fontSize: 12, color: '#6366F1', marginTop: 2 },

  // Chart
  section: { background: '#FFFFFF', borderRadius: 16, padding: '16px', marginBottom: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' },
  sectionTitle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.06em' },
  barChart: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: 100, gap: 4 },
  barCol: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  barCount: { fontSize: 10, color: '#94A3B8', height: 14 },
  bar: { width: '100%', borderRadius: '4px 4px 0 0', transition: 'height 0.3s ease' },
  barLabel: { fontSize: 9, color: '#94A3B8' },
  activeTimeNote: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#94A3B8', marginTop: 12 },

  // Subject
  subjectRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  subjectEmoji: { fontSize: 16, width: 20, textAlign: 'center', flexShrink: 0 },
  subjectName: { fontSize: 13, color: '#374151', width: 72, flexShrink: 0 },
  subjectBarTrack: { flex: 1, height: 8, background: '#F1F5F9', borderRadius: 4, overflow: 'hidden' },
  subjectBarFill: { height: '100%', borderRadius: 4, transition: 'width 0.5s ease' },
  subjectCount: { fontSize: 13, fontWeight: 700, color: '#374151', width: 24, textAlign: 'right' },

  // Add child
  addChildSection: { background: '#FFFFFF', borderRadius: 14, padding: '16px', marginTop: 8 },
  addChildTitle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#4F46E5', marginBottom: 12 },
};
